<template>
    <v-dialog v-model="show" persistent no-click-animation width="1000">
        <v-card style="border-radius: 5px !important;">
            <!--Header dialog-->
            <div class="dialog-header">
                <div>{{ $f.tc('insights') }}</div>
                <v-btn small icon @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </div>
            <!-- Content -->
            <v-tabs v-model="currentTab" style="min-height: 598px !important;" slider-color="#FFFFFF">
                <v-tab ripple>{{ $f.tc('llmSummary') }}</v-tab>
                <v-tab ripple>{{ $f.tc('llmAnalysis') }}</v-tab>
                <v-tabs-items touchless>
                    <!-- LLM Summary -->
                    <v-tab-item>
                        <div class="dialog-content fci-scrollbar">
                            <LLMConfig ref="LLMSummary"
                                       :config="llmSummary"
                                       :allowed-llm-configs="allowedLLMConfigs"
                                       :config-type="'llm_summary'"
                                       @getChanges="getChanges"></LLMConfig>
                        </div>
                    </v-tab-item>
                    <!-- LLM Analysis -->
                    <v-tab-item>
                        <div class="dialog-content fci-scrollbar">
                            <LLMConfig ref="LLMAnalysis"
                                       :config="llmAnalysis"
                                       :allowed-llm-configs="allowedLLMConfigs"
                                       :config-type="'llm_analysis'"
                                       @getChanges="getChanges"></LLMConfig>
                        </div>
                    </v-tab-item>
                </v-tabs-items>
            </v-tabs>
            <!--Footer dialog-->
            <div class="dialog-footer" v-if="isEdit">
                <div class="btn-custom mr-6"
                     @click="saveList">{{ $f.tc('save') }}
                </div>
                <div class="btn-outline" @click="close">{{ $f.tc('cancel') }}</div>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import DialogPromiseMixin from "@/modules/fptai/mixins/DialogPromiseMixin.vue";
import FormMixin from "@/modules/fptai/mixins/FormMixin.vue";
import ScenarioService from "@/services/scenario.service";
import {mapState} from "vuex";
import LLMConfig from "@/views/Scenario/llm/LLMConfig.vue";


export default {
    name: "ScenarioInsight",
    components: {LLMConfig},
    mixins: [DialogPromiseMixin, FormMixin],
    computed: {
        ...mapState({
            treeData: state => state.scenario.treeData,
            scenarioSetting: state => state.scenario.scenario_setting,
            allowedLLMConfigs: state => state.scenario.allowedLLMConfigs,
            llmConfigs: state => state.scenario.llmConfigs,
        }),
        isEdit() {
            return ScenarioService.isEditable() && ScenarioService.isModeEditing()
        },
    },
    data() {
        return {
            currentTab: 0,
            llmSummary: [],
            llmAnalysis: [],
            updateList: [],
            addList: [],
            deleteList: [],
        }
    },
    methods: {
        open() {
            this.loadLLMConfigs()
            this.show = true
            return new Promise((resolve, reject) => {
                this.promise = {resolve, reject}
            })
        },
        loadLLMConfigs() {
            if (this.llmConfigs) {
                this.llmSummary = this.llmConfigs.filter(config => config.type === 'llm_summary')
                this.llmAnalysis = this.llmConfigs.filter(config => config.type === 'llm_analysis')
            }
        },
        getChanges(model) {
            this.addList.push(...model.uniqueAdd)
            this.updateList.push(...model.updateList)
            this.deleteList.push(...model.deleteList)
        },
        saveList() {
            Promise.allSettled([
                this.submitForm('LLMSummary'), // Submit form 1
                this.submitForm('LLMAnalysis')    // Submit form 2
            ])
                .then((results) => {
                    const someRejected = results.some(result => result.status === 'rejected');

                    if (someRejected) {
                        // If any forms failed
                        this.$appService.toastError(this.$f.tc('actionFailedReason', {
                            action: this.$t('update'),
                            item: this.$t('llmConfig'),
                            error: "rejected"
                        }));
                    } else {
                        // If at least both forms succeeded, dispatch some action
                        this.handleUpdate()
                    }
                })
                .catch((error) => {
                    this.$appService.toastError(this.$f.tc('actionFailedReason', {
                        action: this.$t('update'),
                        item: this.$t('llmConfig'),
                        error: error
                    }));
                });
        },
        submitForm(refName) {
            return new Promise((resolve, reject) => {
                const form = this.$refs[refName];
                if (form && typeof form.submit === 'function') {
                    // Set up listeners for 'submit' and 'submit-error' events
                    form.$once('submit', resolve); // Resolve when the form is successfully submitted
                    form.$once('submit-error', reject); // Reject if there's an error during submission
                    form.submit(); // Trigger the form submission
                } else {
                    resolve(); // Resolve immediately if the form doesn't exist or isn't ready
                }
            });
        },
        handleUpdate() {
            let promiseList = this.getPromise()

            let i18n = {
                action: this.$t('update'),
                item: this.$t('llmConfig')
            }

            Promise.allSettled(promiseList).then(
                resultList => {
                    this.$appService.hideLoading()
                    for (let result of resultList) {
                        if (result.status === 'rejected') {
                            this.$appService.toastError(this.$f.tc('actionFailed', i18n))
                            return
                        }
                    }
                    if (resultList.length > 0 && resultList.every(item => item.status !== 'rejected') || resultList.length === 0) {
                        this.$appService.toastSuccess(this.$f.tc('actionSuccessful', i18n))
                        this.$store.dispatch(this.$actionTypes.scenario.GET_LLM_CONFIGS, this.$route.params.scenarioId)
                            .then(() => {
                                this.loadLLMConfigs();
                                this.resetChanges()
                            });
                    }
                }
            )
        },
        getPromise() {
            let promiseList = []
            let scenario_id = this.$route.params.scenarioId

            for (let item of this.addList) {
                promiseList.push(ScenarioService.LLMConfigService.create(scenario_id, item))
            }

            for (let item of this.updateList) {
                promiseList.push(ScenarioService.LLMConfigService.update(scenario_id, item.id, item))
            }

            for (let item of this.deleteList) {
                promiseList.push(ScenarioService.LLMConfigService.delete(scenario_id, item.id))
            }
            return promiseList
        },
        resetChanges() {
            this.addList = []
            this.updateList = []
            this.deleteList = []
        },
    }
}
</script>

<style scoped lang="scss">
::v-deep {
    .CodeMirror {
        height: 150px !important;
    }

    .dialog-content {
        height: calc(80vh - 150px);
        overflow-y: auto;
    }

    .v-tabs__container {
        height: 48px !important;
        border-bottom: 1px solid #E4EDFA;
        padding: 0 12px;
    }

    .v-tabs__item {
        font-family: "open sans semi", serif;
        font-weight: 400 !important;
        height: 32px !important;
        line-height: 32px !important;
        min-width: 0 !important;
        border-radius: 5px;
        text-transform: none;
        font-size: 14px !important;
        padding: 0 12px !important;
        transition: 0.4s;

        &:not(.v-tabs__item--active):hover {
            background: #F2F2F2;
            color: #141927;
        }
    }

    .v-tabs__item--active {
        background: #EEF6FF !important;
        color: #0052CC !important;
    }

    .v-tabs__div {
        text-transform: none !important;
        font-weight: 400 !important;
    }

    .v-input--selection-controls {
        margin-top: 0 !important;
        padding-top: 0 !important;

        .v-messages {
            min-height: 2px !important;
        }
    }

    .v-input--selection-controls:not(.v-input--hide-details) .v-input__slot {
        margin-bottom: 0 !important;
    }

    .v-text-field {
        padding-top: 0 !important;
        margin-top: 0 !important;
    }

    .v-input--slider {
        margin-top: 0 !important;
    }

    .v-slider__thumb {
        width: 18px !important;
        height: 18px !important;
        left: -9px !important;
    }

    .v-slider__thumb-label {
        border-radius: 50% 50% 0;
        transform: translateY(-20%) translateY(-12px) translateX(-50%) rotate(45deg);
    }

    .tab-classifier {
        .v-label {
            font-family: 'open sans', serif !important;
            font-size: 14px !important;
            color: #000;
        }

        .v-input--selection-controls {
            padding-top: 12px !important;
        }

        .v-input--selection-controls__ripple {
            height: 0;
            width: 0;
        }
    }

    .v-text-field__details {
        position: absolute;
        right: 6px;
        top: -18px;
    }

    .v-select {
        .v-input__slot {
            padding: 6px 4px 6px 12px !important;
        }
    }

    .input-custom:has(.error--text):not(.v-input--is-readonly), .textarea-custom:has(.error--text):not(.v-input--is-readonly) {
        .v-input__slot {
            border-color: #ff5252 !important;
        }
    }

    .input-custom.v-input--is-focused:not(.v-input--is-readonly), .textarea-custom.v-input--is-focused:not(.v-input--is-readonly) {
        .v-input__slot {
            outline: 2px solid #FF52526B;
        }

        &:not(.error--text) {
            .v-input__slot {
                outline: 2px solid #0052CC6B;
                border-color: #0052CC !important;
            }
        }
    }
}
</style>
